import React from "react";
import { Box, Flex, HStack, Icon, Skeleton, Stack, Tag, Text } from "@chakra-ui/react";
import { FiActivity, FiAlertTriangle, FiCheckCircle, FiRadio } from "react-icons/fi";

const PharmacySearchState = ({
  hasMedicines,
  locationReady,
  radiusKm,
  loading,
  error,
  matchesCount,
}) => {
  if (loading) {
    return (
      <Box
        border="1px solid #dbeafe"
        borderRadius="16px"
        p={{ base: 4, lg: 4.5 }}
        bg="linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)"
      >
        <Stack spacing={3.5}>
          <HStack spacing={2} color="#1d4ed8" align="center" justify="space-between">
            <HStack spacing={2}>
              <Icon as={FiRadio} />
              <Text fontWeight="800" m={0}>
                Checking nearby pharmacies
              </Text>
            </HStack>
            <Tag borderRadius="full" bg="#dbeafe" color="#1d4ed8" fontWeight="800" px={3}>
              Live
            </Tag>
          </HStack>
          <Text fontSize="sm" color="#64748b" m={0} maxW="720px">
            Checking stock and prices at pharmacies within {radiusKm} km of your delivery address.
          </Text>
          <Stack spacing={2}>
            <Skeleton height="12px" borderRadius="full" />
            <Skeleton height="12px" borderRadius="full" width="82%" />
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Flex
        border="1px solid #fecaca"
        bg="#fff7f7"
        color="#b91c1c"
        borderRadius="16px"
        p={4}
        align="flex-start"
        gap={3}
      >
        <Icon as={FiAlertTriangle} mt={0.5} />
        <Box>
          <Text fontSize="sm" fontWeight="800" m={0}>
            Could not check pharmacies
          </Text>
          <Text fontSize="sm" m={0}>
            {error}
          </Text>
        </Box>
      </Flex>
    );
  }

  if (matchesCount > 0) {
    return (
      <Box
        border="1px solid #dbeafe"
        borderRadius="16px"
        p={{ base: 4, lg: 4.5 }}
        bg="linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)"
      >
        <Stack spacing={3.5}>
          <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" gap={3} wrap="wrap">
            <HStack spacing={2}>
              <Icon as={FiCheckCircle} color="#0f766e" />
              <Text fontWeight="800" color="#0f172a" m={0}>
                Live offers received
              </Text>
            </HStack>
            <HStack spacing={2} flexWrap="wrap">
              <Tag borderRadius="full" bg="#dcfce7" color="#166534" fontWeight="800" px={3}>
                {matchesCount} live match{matchesCount > 1 ? "es" : ""}
              </Tag>
              <Tag borderRadius="full" bg="#eef2ff" color="#4338ca" fontWeight="800" px={3}>
                Radius {radiusKm} km
              </Tag>
            </HStack>
          </Flex>
          <Text fontSize="sm" color="#475569" m={0} maxW="760px">
            We ranked nearby responses by medicine match, missing items, total amount, and distance so you can choose quickly.
          </Text>
          <Flex gap={2} wrap="wrap">
            <Tag borderRadius="full" bg="#f8fafc" color="#475569">
              Match first
            </Tag>
            <Tag borderRadius="full" bg="#f8fafc" color="#475569">
              Price next
            </Tag>
            <Tag borderRadius="full" bg="#f8fafc" color="#475569">
              Distance as tie-breaker
            </Tag>
          </Flex>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      border="1px solid #e2e8f0"
      borderRadius="16px"
      p={{ base: 4, lg: 4.5 }}
      bg="linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)"
    >
      <Stack spacing={3.5}>
        <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" gap={3} wrap="wrap">
          <HStack spacing={2}>
            <Icon as={FiActivity} color="#4338ca" />
            <Text fontWeight="800" color="#0f172a" m={0}>
              Live pharmacy search is ready
            </Text>
          </HStack>
          <HStack spacing={2} flexWrap="wrap">
            <Tag borderRadius="full" bg={hasMedicines ? "#dcfce7" : "#f8fafc"} color={hasMedicines ? "#166534" : "#475569"} fontWeight="800">
              Medicines {hasMedicines ? "ready" : "pending"}
            </Tag>
            <Tag borderRadius="full" bg={locationReady ? "#dcfce7" : "#f8fafc"} color={locationReady ? "#166534" : "#475569"} fontWeight="800">
              Location {locationReady ? "ready" : "pending"}
            </Tag>
            <Tag borderRadius="full" bg="#eef2ff" color="#4338ca" fontWeight="800">
              Radius {radiusKm} km
            </Tag>
          </HStack>
        </Flex>
        <Text fontSize="sm" color="#64748b" m={0} maxW="760px">
          {hasMedicines
            ? locationReady
              ? `Your draft is ready. Check connected pharmacies within ${radiusKm} km to compare live offers.`
              : "Set the delivery pin to start comparing nearby connected pharmacies."
            : "Add medicines first, then the network search will broadcast your basket to nearby connected pharmacies."}
        </Text>
      </Stack>
    </Box>
  );
};

export default PharmacySearchState;
